import React, { useState } from "react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import useReveal from "../Components/useReveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faHeart,
  faHandshakeAngle,
  faUserPlus,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Styles/Forms.css";
import asideImg from "../Images/image2.jpg";

const skillsList = [
  "Companion Care & Safety",
  "Medication Reminders",
  "Oral Care",
  "Alzheimer's",
  "Transportation",
  "Dementia",
  "Meal Prep / Clean up",
  "Dressing / Grooming",
  "Transfer Assist",
  "Shaving Assistance",
  "Bathing (Reg, Bed, Sponge)",
  "Assist w/ P.T Exercises",
  "Hospice",
  "Feeding",
  "Incontinence",
  "Light Housekeeping",
  "Ambulation",
  "Laundry",
];

function Apply() {
  useReveal();
  const [skills, setSkills] = useState([]);
  const [sending, setSending] = useState(false);

  const toggleSkill = (skill) => {
    setSkills((prev) =>
      prev.includes(skill)
        ? prev.filter((s) => s !== skill)
        : [...prev, skill]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formEl = e.target;
    const payload = Object.fromEntries(new FormData(formEl).entries());
    payload.skills = skills;
    setSending(true);
    try {
      const res = await fetch("/api/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success(
        "Thank you for applying! Our team will review your application and reach out soon.",
        { position: "top-center" }
      );
      formEl.reset();
      setSkills([]);
    } catch {
      toast.error("Something went wrong. Please try again or call us.", {
        position: "top-center",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="apply-page">
      <Navbar />
      <ToastContainer />

      <section className="page-banner">
        <div className="hr-container">
          <span className="hr-eyebrow">Careers</span>
          <h1>Apply with Us</h1>
          <p>
            Join a compassionate team dedicated to enhancing lives. If caring
            for others is your calling, we'd love to hear from you.
          </p>
          <div className="page-crumb">
            <a href="/">Home</a> / Apply with Us
          </div>
        </div>
      </section>

      <section className="hr-section">
        <div className="hr-container">
          <div className="form-layout">
            {/* Aside */}
            <aside className="form-aside reveal">
              <div className="form-aside-photo">
                <img src={asideImg} alt="Smiling caregiver" />
              </div>
              <div className="form-aside-inner">
              <h3>Become a Caregiver</h3>
              <p>
                Make a real difference in people's lives while building a
                rewarding career with a team that values compassion.
              </p>
              <div className="form-aside-item">
                <span className="fa-icon">
                  <FontAwesomeIcon icon={faHeart} />
                </span>
                <div>
                  <span>Meaningful Work</span>
                  <strong>Care that matters</strong>
                </div>
              </div>
              <div className="form-aside-item">
                <span className="fa-icon">
                  <FontAwesomeIcon icon={faHandshakeAngle} />
                </span>
                <div>
                  <span>Supportive Team</span>
                  <strong>We've got your back</strong>
                </div>
              </div>
              <div className="form-aside-item">
                <span className="fa-icon">
                  <FontAwesomeIcon icon={faPhone} />
                </span>
                <div>
                  <span>Questions?</span>
                  <a href="tel:2535230098">253-523-0098</a>
                </div>
              </div>
              <div className="form-aside-item">
                <span className="fa-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
                <div>
                  <span>Email</span>
                  <a
                    href="mailto:info@harmonyrisehomecare.com"
                    className="form-aside-email"
                  >
                    info@harmonyrisehomecare.com
                  </a>
                </div>
              </div>
              </div>
            </aside>

            {/* Form */}
            <form className="form-card reveal" onSubmit={handleSubmit}>
              {/* Personal */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <span className="fs-num">1</span> Personal Information
                </h3>
                <div className="form-grid grid-2">
                  <div className="field">
                    <label>
                      Your Full Name <span className="req">*</span>
                    </label>
                    <input name="fullName" type="text" required placeholder="Full name" />
                  </div>
                  <div className="field">
                    <label>
                      Date of Application <span className="req">*</span>
                    </label>
                    <input name="applicationDate" type="date" required />
                  </div>
                </div>
                <div className="form-grid" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>
                      Street Address <span className="req">*</span>
                    </label>
                    <input name="streetAddress" type="text" required placeholder="Address line 1" />
                  </div>
                </div>
                <div className="form-grid grid-3" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>City</label>
                    <input name="city" type="text" placeholder="City" />
                  </div>
                  <div className="field">
                    <label>State / Region</label>
                    <input name="state" type="text" placeholder="State" />
                  </div>
                  <div className="field">
                    <label>Zip / Postal Code</label>
                    <input name="zip" type="text" placeholder="ZIP" />
                  </div>
                </div>
                <div className="form-grid grid-3" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>
                      Home Phone <span className="req">*</span>
                    </label>
                    <input name="homePhone" type="tel" required placeholder="Home phone" />
                  </div>
                  <div className="field">
                    <label>
                      Cell Phone <span className="req">*</span>
                    </label>
                    <input name="cellPhone" type="tel" required placeholder="Cell phone" />
                  </div>
                  <div className="field">
                    <label>Date of Birth</label>
                    <input name="dob" type="date" />
                  </div>
                </div>
                <div className="form-grid grid-2" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>
                      Do you smoke? <span className="req">*</span>
                    </label>
                    <div className="choice-row">
                      <label className="choice">
                        <input type="radio" name="smokes" value="Yes" required /> Yes
                      </label>
                      <label className="choice">
                        <input type="radio" name="smokes" value="No" /> No
                      </label>
                    </div>
                  </div>
                  <div className="field">
                    <label>
                      How did you hear about us? <span className="req">*</span>
                    </label>
                    <input name="heardAbout" type="text" required placeholder="e.g. Referral, online" />
                  </div>
                </div>
              </div>

              {/* Education */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <span className="fs-num">2</span> Education
                </h3>
                <div className="form-grid grid-2">
                  <div className="field">
                    <label>School, Location</label>
                    <input name="school" type="text" placeholder="School name and location" />
                  </div>
                  <div className="field">
                    <label>Degree / Diploma</label>
                    <input name="degree" type="text" placeholder="Degree or diploma" />
                  </div>
                </div>
                <div className="form-grid grid-2" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>Course of Study</label>
                    <input name="course" type="text" placeholder="Course of study" />
                  </div>
                  <div className="field">
                    <label>Dates Attended</label>
                    <input name="eduDates" type="text" placeholder="From – To" />
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <span className="fs-num">3</span> Skills
                </h3>
                <div className="checkbox-grid">
                  {skillsList.map((skill) => (
                    <label className="checkbox-item" key={skill}>
                      <input
                        type="checkbox"
                        checked={skills.includes(skill)}
                        onChange={() => toggleSkill(skill)}
                      />
                      {skill}
                    </label>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <span className="fs-num">4</span> Availability &amp; Transport
                </h3>
                <div className="form-grid grid-2">
                  <div className="field">
                    <label>
                      Approx. hours per week available{" "}
                      <span className="req">*</span>
                    </label>
                    <input name="hoursAvailable" type="text" required placeholder="e.g. 30 hours" />
                  </div>
                  <div className="field">
                    <label>
                      Days / times you are available{" "}
                      <span className="req">*</span>
                    </label>
                    <input name="daysAvailable" type="text" required placeholder="e.g. Mon–Fri, mornings" />
                  </div>
                </div>
                <div className="form-grid grid-2" style={{ marginTop: "20px" }}>
                  <div className="field">
                    <label>
                      Available on short notice for emergencies?{" "}
                      <span className="req">*</span>
                    </label>
                    <div className="choice-row">
                      <label className="choice">
                        <input type="radio" name="emergencyAvailable" value="Yes" required /> Yes
                      </label>
                      <label className="choice">
                        <input type="radio" name="emergencyAvailable" value="No" /> No
                      </label>
                    </div>
                  </div>
                  <div className="field">
                    <label>
                      Do you have dependable transportation?{" "}
                      <span className="req">*</span>
                    </label>
                    <div className="choice-row">
                      <label className="choice">
                        <input type="radio" name="hasTransport" value="Yes" required /> Yes
                      </label>
                      <label className="choice">
                        <input type="radio" name="hasTransport" value="No" /> No
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* General */}
              <div className="form-section">
                <h3 className="form-section-title">
                  <span className="fs-num">5</span> General Information
                </h3>
                <div className="form-grid">
                  <div className="field">
                    <label>
                      Are you legally authorized to work in the USA?{" "}
                      <span className="req">*</span>
                    </label>
                    <div className="choice-row">
                      <label className="choice">
                        <input type="radio" name="workAuthorized" value="Yes" required /> Yes
                      </label>
                      <label className="choice">
                        <input type="radio" name="workAuthorized" value="No" /> No
                      </label>
                    </div>
                    <p className="field-hint">
                      If hired, you will be required to provide documentation of
                      eligibility to work in the US.
                    </p>
                  </div>
                  <div className="field">
                    <label>Work History (most recent)</label>
                    <textarea name="workHistory" placeholder="Company, dates, job title, duties, reason for leaving..." />
                  </div>
                  <div className="field">
                    <label>References</label>
                    <textarea name="references" placeholder="Please list three references (name, company, title, phone)..." />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="hr-btn hr-btn-primary hr-btn-lg form-submit"
                disabled={sending}
              >
                <FontAwesomeIcon icon={faUserPlus} />{" "}
                {sending ? "Submitting..." : "Submit Application"}
              </button>
              <p className="form-note">
                <FontAwesomeIcon icon={faPaperPlane} /> Your information is kept
                confidential and used only for hiring.
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Apply;
